import React, { useState, useEffect, useCallback } from 'react';
import { 
  getDashboardStats, 
  getBlogPosts, 
  createBlogPost, 
  updateBlogPost, 
  deleteBlogPost,
  uploadImage,
  getContacts
} from '../services/api';
import { ArrowLeft, RefreshCw, Plus } from 'lucide-react';
import { OverviewTab, BlogPostEditor, BlogPostList, ContactsTab } from './admin';

const EMPTY_POST = {
  title: '',
  titleEs: '',
  titleFr: '',
  excerpt: '',
  excerptEs: '',
  excerptFr: '',
  content: '',
  contentEs: '',
  contentFr: '',
  image: '',
  category: '',
  categoryEs: '',
  categoryFr: '',
  published: true
};

const AdminDashboard = ({ onBack }) => {
  const [stats, setStats] = useState(null);
  const [posts, setPosts] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [editingPost, setEditingPost] = useState(null);
  const [isCreating, setIsCreating] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [statsData, postsData, contactsData] = await Promise.all([
        getDashboardStats(),
        getBlogPosts(),
        getContacts()
      ]);
      setStats(statsData);
      setPosts(postsData);
      setContacts(contactsData);
    } catch (err) {
      setError('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploading(true);
    try {
      const result = await uploadImage(file);
      const imageUrl = `${process.env.REACT_APP_BACKEND_URL}${result.url}`;
      setEditingPost(prev => ({ ...prev, image: imageUrl }));
    } catch (err) {
      setError('Failed to upload image');
    } finally {
      setUploading(false);
    }
  };

  const handleSavePost = async () => {
    try {
      if (isCreating) {
        await createBlogPost(editingPost);
      } else {
        await updateBlogPost(editingPost.id, editingPost);
      }
      setEditingPost(null);
      setIsCreating(false);
      fetchData();
    } catch (err) {
      setError('Failed to save blog post');
    }
  };

  const handleDeletePost = async (id) => {
    if (!window.confirm('Are you sure you want to delete this post?')) return;
    try {
      await deleteBlogPost(id);
      fetchData();
    } catch (err) {
      setError('Failed to delete blog post');
    }
  };

  const handleCloseEditor = () => {
    setEditingPost(null);
    setIsCreating(false);
  };

  const handleEditPost = (post) => {
    setEditingPost(post);
    setIsCreating(false);
  };

  const handleCreatePost = () => {
    setIsCreating(true);
    setEditingPost(EMPTY_POST);
    setActiveTab('blog');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <RefreshCw className="w-8 h-8 animate-spin text-teal-600 mx-auto mb-4" />
          <p className="text-gray-600">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button 
              onClick={onBack}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              data-testid="admin-back-btn"
            >
              <ArrowLeft className="w-5 h-5 text-gray-600" />
            </button>
            <h1 className="text-xl font-bold text-gray-800">Admin Dashboard</h1>
          </div>
          <button 
            onClick={fetchData}
            className="flex items-center gap-2 px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
            data-testid="admin-refresh-btn"
          >
            <RefreshCw className="w-4 h-4" />
            Refresh
          </button>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 py-8">
        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
            {error}
            <button onClick={() => setError(null)} className="ml-2 underline">Dismiss</button>
          </div>
        )}

        {/* Navigation Tabs */}
        <div className="flex gap-2 mb-8">
          {['overview', 'blog', 'contacts'].map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                activeTab === tab 
                  ? 'bg-teal-600 text-white' 
                  : 'bg-white text-gray-600 hover:bg-gray-100'
              }`}
              data-testid={`admin-tab-${tab}`}
            >
              {tab.charAt(0).toUpperCase() + tab.slice(1)}
            </button>
          ))}
        </div>

        {/* Overview Tab */}
        {activeTab === 'overview' && (
          <OverviewTab 
            stats={stats}
            onCreatePost={handleCreatePost}
            onViewContacts={() => setActiveTab('contacts')}
          />
        )}

        {/* Blog Tab */}
        {activeTab === 'blog' && (
          <div className="space-y-6">
            <BlogPostEditor
              editingPost={editingPost}
              setEditingPost={setEditingPost}
              isCreating={isCreating}
              onSave={handleSavePost}
              onClose={handleCloseEditor}
              onImageUpload={handleImageUpload}
              uploading={uploading}
            />

            {/* Add New Post Button */}
            {!editingPost && !isCreating && (
              <button
                onClick={() => { setIsCreating(true); setEditingPost(EMPTY_POST); }}
                className="flex items-center gap-2 px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors"
                data-testid="add-post-btn"
              >
                <Plus className="w-4 h-4" />
                Add New Post
              </button>
            )}

            <BlogPostList
              posts={posts}
              onEdit={handleEditPost}
              onDelete={handleDeletePost}
            />
          </div>
        )}

        {/* Contacts Tab */}
        {activeTab === 'contacts' && (
          <ContactsTab contacts={contacts} />
        )}
      </div>
    </div>
  );
};

export default AdminDashboard;
